import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, RefreshCw, ExternalLink, Calendar, Repeat, ArrowLeft } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function fmtINR(n) { if (!n && n !== 0) return "—"; return "₹" + Math.round(n).toLocaleString("en-IN"); }
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso; }
}
function adminHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("admin_token") || ""}` };
}

const STATUS_COLORS = {
  active:    "bg-emerald-500/15 border-emerald-400/40 text-emerald-200",
  cancelled: "bg-red-500/15 border-red-400/40 text-red-200",
  renewed:   "bg-cyan-500/15 border-cyan-400/40 text-cyan-200",
  paused:    "bg-amber-500/15 border-amber-400/40 text-amber-200",
};

export default function AdminSubscriptions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [tickRunning, setTickRunning] = useState(false);
  const [tickMsg, setTickMsg] = useState("");

  async function load() {
    setLoading(true);
    try {
      const q = filter === "all" ? "" : `?status=${filter}`;
      const r = await axios.get(`${API}/admin/subscriptions${q}`, { headers: adminHeaders() });
      setItems(r.data?.subscriptions || []);
    } catch (_) { setItems([]); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  async function runDripTick() {
    setTickRunning(true); setTickMsg("");
    try {
      const r = await axios.post(`${API}/admin/subscriptions-drip-tick`, {}, { headers: adminHeaders() });
      setTickMsg(`Drip ran: ${JSON.stringify(r.data?.counts || {})}`);
    } catch (e) {
      setTickMsg("Drip failed: " + (e?.response?.data?.detail || e?.message));
    } finally { setTickRunning(false); }
  }

  async function patchStatus(subId, newStatus) {
    if (!confirm(`Mark ${subId} as "${newStatus}"?`)) return;
    try {
      await axios.patch(`${API}/admin/subscriptions/${subId}`, { status: newStatus }, { headers: adminHeaders() });
      load();
    } catch (e) {
      alert("Failed: " + (e?.response?.data?.detail || e?.message));
    }
  }

  const stats = {
    all: items.length,
    active: items.filter(s => s.status === "active").length,
    cancelled: items.filter(s => s.status === "cancelled").length,
    upcoming: items.filter(s => s.status === "active" && s.renews_at && new Date(s.renews_at) - new Date() <= 30 * 86400 * 1000).length,
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white px-4 sm:px-6 py-6" data-testid="admin-subscriptions">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link to="/admin/quotes" className="text-zinc-500 hover:text-cyan-300 inline-flex items-center gap-1 text-sm">
              <ArrowLeft size={14}/> Back to Quotes
            </Link>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Repeat size={20} className="text-emerald-300"/> Annual Subscriptions
            </h1>
          </div>
          <button
            onClick={runDripTick}
            disabled={tickRunning}
            data-testid="run-drip-tick"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-zinc-300 text-xs font-bold hover:border-cyan-400 disabled:opacity-40"
          >
            {tickRunning ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>}
            Run reminder tick
          </button>
        </div>
        {tickMsg && <p className="text-xs text-cyan-300 mb-4" data-testid="drip-msg">{tickMsg}</p>}

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { k: "all", label: "Total", val: stats.all, color: "text-white" },
            { k: "active", label: "Active", val: stats.active, color: "text-emerald-300" },
            { k: "upcoming", label: "Due ≤30d", val: stats.upcoming, color: "text-amber-300" },
            { k: "cancelled", label: "Cancelled", val: stats.cancelled, color: "text-red-300" },
          ].map(({k, label, val, color}) => (
            <div key={k} className="bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3" data-testid={`stat-${k}`}>
              <p className="text-xs text-zinc-500 uppercase tracking-wider">{label}</p>
              <p className={`text-2xl font-bold mt-1 ${color}`}>{val}</p>
            </div>
          ))}
        </div>

        {/* Filter pills */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {["all", "active", "cancelled", "renewed", "paused"].map((f) => (
            <button
              key={f}
              onClick={()=>setFilter(f)}
              data-testid={`filter-${f}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border ${filter===f ? "bg-cyan-500/15 border-cyan-400 text-cyan-200" : "bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-zinc-500"}`}
            >
              {f}
            </button>
          ))}
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-16"><Loader2 size={20} className="animate-spin text-zinc-500 inline"/></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-zinc-500 text-sm" data-testid="empty-state">No subscriptions yet.</div>
        ) : (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-800/50 text-xs uppercase tracking-wider text-zinc-400">
                  <tr>
                    <th className="text-left px-4 py-3">Sub ID</th>
                    <th className="text-left px-4 py-3">Customer</th>
                    <th className="text-left px-4 py-3">Quote</th>
                    <th className="text-right px-4 py-3">Price</th>
                    <th className="text-left px-4 py-3">Renews</th>
                    <th className="text-left px-4 py-3">Reminders</th>
                    <th className="text-center px-4 py-3">Status</th>
                    <th className="text-center px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800">
                  {items.map((s) => {
                    const renewsAt = s.renews_at;
                    const daysLeft = renewsAt ? Math.ceil((new Date(renewsAt) - new Date()) / 86400 / 1000) : null;
                    return (
                      <tr key={s.subscription_id} className="hover:bg-zinc-800/30" data-testid={`row-${s.subscription_id}`}>
                        <td className="px-4 py-3 font-mono text-xs text-zinc-300">{s.subscription_id}</td>
                        <td className="px-4 py-3">
                          <p className="text-white">{s.customer?.full_name || "—"}</p>
                          <p className="text-[11px] text-zinc-500">{s.customer?.email}</p>
                        </td>
                        <td className="px-4 py-3 text-zinc-400 text-xs font-mono">{s.quote_ref}</td>
                        <td className="px-4 py-3 text-right">
                          <p className="text-emerald-300 font-bold">{fmtINR(s.plan?.renewal_price)}/yr</p>
                          <p className="text-[11px] text-zinc-500">~ {fmtINR(s.plan?.monthly_equiv)}/mo</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-white text-xs flex items-center gap-1"><Calendar size={11}/> {fmtDate(renewsAt)}</p>
                          {daysLeft != null && s.status === "active" && (
                            <p className={`text-[11px] mt-0.5 ${daysLeft <= 14 ? "text-amber-300" : "text-zinc-500"}`}>
                              {daysLeft > 0 ? `in ${daysLeft}d` : daysLeft === 0 ? "today" : `${-daysLeft}d overdue`}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-[11px] text-zinc-400">{(s.renewal_reminders_sent || []).join(", ") || "—"}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold border ${STATUS_COLORS[s.status] || "bg-zinc-700/40 border-zinc-600 text-zinc-400"}`}>
                            {s.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex gap-1.5 justify-center">
                            <Link to={`/subscriptions/${s.cancel_token}`} target="_blank" className="text-cyan-300 hover:text-cyan-200" title="View customer page" data-testid={`view-${s.subscription_id}`}>
                              <ExternalLink size={14}/>
                            </Link>
                            {s.status === "active" && (
                              <button onClick={()=>patchStatus(s.subscription_id, "cancelled")} className="text-red-300 hover:text-red-200 text-[11px]" data-testid={`cancel-${s.subscription_id}`}>
                                Cancel
                              </button>
                            )}
                            {s.status === "cancelled" && (
                              <button onClick={()=>patchStatus(s.subscription_id, "active")} className="text-emerald-300 hover:text-emerald-200 text-[11px]" data-testid={`reactivate-${s.subscription_id}`}>
                                Reactivate
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
